import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { patientQueryKeys, patientsApi } from "../../shared/api/patients";
import { hisIntegrationApi, hisIntegrationQueryKeys } from "../../shared/api/hisIntegration";
import { apiError } from "../../shared/api/client";
import { PatientHeader } from "./components/PatientHeader";
import { OverviewPanel } from "./components/OverviewPanel";
import { MembershipCard } from "./components/MembershipCard";
import { HisExamResultsCard } from "./components/HisExamResultsCard";
import { HisPatientLookupCard } from "./components/HisPatientLookupCard";
import { HisMedicalHistoryCard } from "./components/HisMedicalHistoryCard";
import { AllergiesPanel } from "./components/AllergiesPanel";
import { IdentifiersPanel } from "./components/IdentifiersPanel";
import { ConditionsPanel } from "./components/ConditionsPanel";
import { AlertsPanel } from "./components/AlertsPanel";
import { TagsPanel } from "./components/TagsPanel";
import { PatientPackagesSection } from "./components/PatientPackagesSection";
import { useOperatesModule } from "../../shared/hooks/useOperatesModule";
import { usePlanScreens } from "../../shared/screens/usePlanScreens";
import { PatientDepositSection } from "./components/PatientDepositSection";
import { PatientLoyaltySection } from "./components/PatientLoyaltySection";
import { TreatmentPlansSection } from "./components/TreatmentPlansSection";
import { PatientRelationsSection } from "./components/PatientRelationsSection";
import {
  clinicalPhotoQueryKeys,
  clinicalPhotosApi,
} from "../../shared/api/clinicalPhotos";
import { ClinicalPhotoPanel } from "./components/ClinicalPhotoPanel";
import { DentalChartPanel } from "./components/DentalChartPanel";
import { dentalApi, dentalQueryKeys } from "../../shared/api/dental";

type Tab =
  | "overview"
  | "identifiers"
  | "allergies"
  | "conditions"
  | "alerts"
  | "tags"
  | "relatives"
  | "packages"
  | "deposit"
  | "loyalty"
  | "plans"
  | "dental"
  | "photos";

const TAB_LABELS: Record<Tab, string> = {
  overview: "Tổng quan",
  identifiers: "Giấy tờ",
  allergies: "Dị ứng",
  conditions: "Bệnh mãn tính",
  alerts: "Cảnh báo",
  tags: "Tag",
  relatives: "Người thân",
  packages: "Gói liệu trình",
  deposit: "Tạm ứng",
  loyalty: "Điểm tích luỹ",
  plans: "Kế hoạch điều trị",
  dental: "Nha khoa",
  photos: "Ảnh điều trị",
};

/**
 * Màn hình sở hữu gói liệu trình (ADR-0053): Cài đặt › Gói liệu trình. Tab "Gói liệu trình" trên
 * hồ sơ bệnh nhân đi theo màn hình này — gói không có nó thì không có gói nào để bán, và tab chỉ còn
 * là lời mời "Tạo ở Cài đặt → Gói liệu trình" dẫn tới trang 402.
 */
const PACKAGES_SCREEN_PATH = "/settings/packages";

/**
 * Mã màn hình của tab "Điểm tích luỹ" (`Screen.PATIENT_LOYALTY` phía máy chủ). Tab này không có
 * đường dẫn riêng — nó sống bên trong /patients/:id — nên là màn hình duy nhất ở đây phải hỏi theo
 * mã thay vì theo đường dẫn; quản trị viên tick/bỏ tick nó như mọi màn hình khác.
 */
const LOYALTY_SCREEN_CODE = "patient-loyalty";

export function PatientDetailPage() {
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");

  const patient = useQuery({
    queryKey: patientQueryKeys.detail(id),
    queryFn: () => patientsApi.get(id),
  });

  const alerts = useQuery({
    queryKey: patientQueryKeys.alerts(id),
    queryFn: () => patientsApi.listAlerts(id),
  });

  // Is this tenant bound to an external HIS? (same check the backend uses) — gates the HSBA card.
  const binding = useQuery({
    queryKey: hisIntegrationQueryKeys.binding(),
    queryFn: () => hisIntegrationApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const hasHis = binding.data ? binding.data.hisType !== "NATIVE" && binding.data.enabled : false;

  // Odontogram tab shows ONLY for a dental-vertical clinic (#18636) — same gate the backend enforces.
  // Thẻ nha khoa, ảnh lâm sàng và kế hoạch điều trị thuộc vùng Khám bệnh; tạm ứng thuộc vùng Tài
  // chính. Phòng khám không dùng vùng đó — vì chế độ vận hành (ADR-0048) hay vì gói (ADR-0053) —
  // thì không hỏi và không vẽ: hỏi là nhận 409/402 ngay trong trang Bệnh nhân mà họ được dùng.
  const encounters = useOperatesModule("ENCOUNTERS");
  const billing = useOperatesModule("BILLING");
  const usesEncounters = encounters.loaded && encounters.operates;
  const usesBilling = billing.loaded && billing.operates;
  // Gói liệu trình không thuộc vùng nào — nó là một màn hình được tick. Hỏi theo đường dẫn như
  // menu và bộ chặn đường dẫn, không theo mã gói: phòng khám có thể được "chỉnh riêng" từng màn.
  const plan = usePlanScreens();
  const usesPackages = plan.settled && plan.allowsPath(PACKAGES_SCREEN_PATH);
  const usesLoyalty = plan.settled && plan.allowsScreen(LOYALTY_SCREEN_CODE);

  const dentalCap = useQuery({
    queryKey: dentalQueryKeys.capability(),
    queryFn: () => dentalApi.capability(),
    staleTime: 5 * 60 * 1000,
    enabled: usesEncounters,
  });
  const dentalEnabled = dentalCap.data?.enabled ?? false;

  // Clinical photo timeline (#18638) — same capability-gated pattern as the dental tab, so the
  // feature simply is not there outside the verticals that use it.
  const photoCap = useQuery({
    queryKey: clinicalPhotoQueryKeys.capability(),
    queryFn: () => clinicalPhotosApi.capability(),
    staleTime: 5 * 60 * 1000,
    enabled: usesEncounters,
  });
  const photosEnabled = photoCap.data?.enabled ?? false;

  const archive = useMutation({
    mutationFn: () => patientsApi.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: patientQueryKeys.detail(id) }),
  });
  const restore = useMutation({
    mutationFn: () => patientsApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: patientQueryKeys.detail(id) }),
  });
  const deleteOne = useMutation({
    mutationFn: () => patientsApi.softDelete(id),
    onSuccess: () => navigate("/patients"),
  });

  // Guard AFTER hooks so hook order is stable (Rules of Hooks / React #310).
  if (!rawId) return <Navigate to="/patients" replace />;

  if (patient.isLoading) {
    return (
      <div className="font-sans">
        <h1 className="text-xl font-semibold text-foreground">Đang tải hồ sơ…</h1>
      </div>
    );
  }
  if (patient.isError || !patient.data) {
    return (
      <div className="font-sans flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Không tìm thấy bệnh nhân</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {patient.error ? apiError(patient.error).detail : "Hồ sơ không tồn tại."}
          </p>
        </div>
        <Button variant="secondary" size="sm" asChild>
          <Link to="/patients">
            <ArrowLeft aria-hidden="true" />
            Danh sách
          </Link>
        </Button>
      </div>
    );
  }

  const p = patient.data;
  const isArchived = p.status === "ARCHIVED";
  // #18862 — a merged record is a pointer, not a patient. It stays readable so old references
  // still resolve, but it must never look like somewhere to carry on working.
  const mergedInto = p.status === "MERGED" ? p.mergedIntoId : null;

  return (
    <div className="font-sans">
      {mergedInto && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
          <span>
            Hồ sơ này đã được <strong>gộp</strong> vào một hồ sơ khác. Mọi thông tin mới phải ghi
            trên hồ sơ chính.
          </span>
          <Button variant="secondary" size="sm" asChild>
            <Link to={`/patients/${mergedInto}`}>Mở hồ sơ chính</Link>
          </Button>
        </div>
      )}
      <PatientHeader
        patient={p}
        alerts={alerts.data ?? []}
        actions={
          <>
            {!mergedInto && (
              <Button variant="secondary" size="sm" asChild>
                <Link to={`/patients/${id}/edit`}>Sửa</Link>
              </Button>
            )}
            <Button variant="secondary" size="sm" asChild>
              <Link to={`/patients/${id}/history`}>Lịch sử khám</Link>
            </Button>
            {!isArchived ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={archive.isPending}
                onClick={() => {
                  if (confirm("Lưu trữ hồ sơ này? Có thể khôi phục sau.")) archive.mutate();
                }}
              >
                Lưu trữ
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={restore.isPending}
                onClick={() => restore.mutate()}
              >
                Khôi phục
              </Button>
            )}
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={deleteOne.isPending}
              onClick={() => {
                if (
                  confirm(
                    "Xoá mềm hồ sơ? Dữ liệu vẫn được giữ cho audit và có thể khôi phục."
                  )
                ) {
                  deleteOne.mutate();
                }
              }}
            >
              Xoá
            </Button>
          </>
        }
      />

      <div
        className="mt-6 flex flex-wrap gap-1 border-b border-border"
        role="tablist"
        aria-label="Mục hồ sơ"
      >
        {(Object.keys(TAB_LABELS) as Tab[])
          .filter((t) => t !== "packages" || usesPackages)
          .filter((t) => t !== "loyalty" || usesLoyalty)
          .filter((t) => t !== "dental" || dentalEnabled)
          .filter((t) => t !== "photos" || photosEnabled)
          .filter((t) => t !== "plans" || usesEncounters)
          .filter((t) => t !== "deposit" || usesBilling)
          .map((t) => (
          <button
            key={t}
            role="tab"
            type="button"
            className={cn(
              "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              tab === t
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
            aria-selected={tab === t}
            onClick={() => setTab(t)}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" && (
          <div className="space-y-6">
            <OverviewPanel patient={p} />
            {/* Renders nothing without module.membership — Pro-only (#18734). */}
            <MembershipCard patientId={id} />
            <HisPatientLookupCard patientId={id} patientCode={p.patientCode} hasHis={hasHis} />
            <HisMedicalHistoryCard patientId={id} patientCode={p.patientCode} hasHis={hasHis} />
            <HisExamResultsCard patientId={id} patientCode={p.patientCode} hasHis={hasHis} />
          </div>
        )}
        {tab === "identifiers" && <IdentifiersPanel patientId={id} />}
        {tab === "allergies" && <AllergiesPanel patientId={id} />}
        {tab === "conditions" && <ConditionsPanel patientId={id} />}
        {tab === "alerts" && <AlertsPanel patientId={id} />}
        {tab === "tags" && <TagsPanel patientId={id} />}
        {tab === "relatives" && <PatientRelationsSection patientId={id} />}
        {tab === "packages" && usesPackages && <PatientPackagesSection patientId={id} />}
        {tab === "deposit" && usesBilling && <PatientDepositSection patientId={id} />}
        {tab === "loyalty" && usesLoyalty && <PatientLoyaltySection patientId={id} />}
        {tab === "plans" && usesEncounters && <TreatmentPlansSection patientId={id} />}
        {tab === "dental" && dentalEnabled && <DentalChartPanel patientId={id} />}
        {tab === "photos" && photosEnabled && <ClinicalPhotoPanel patientId={id} />}
      </div>
    </div>
  );
}
